import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { TemporalDayOfWeek } from "@redotech/util/temporal";
import { assertNever } from "@redotech/util/type";

/**
 * Generic weekend + US-holiday business-day helpers.
 *
 * The `businessEntity` parameter selects which holiday calendar to apply. A
 * shipping `Carriers` value is one such entity (FedEx, UPS, and USPS observe
 * slightly different holiday sets); `undefined` uses a general US-holiday
 * calendar for callers that aren't tied to a carrier (e.g. PP+ approval timing).
 *
 * Genuinely carrier-specific delivery-date validation lives in
 * `../conversion/delivery-date/valid-delivery-date.ts`.
 */

export function maybeIncrementToNextBusinessDay({
  date,
  businessEntity,
}: {
  date: Temporal.ZonedDateTime;
  businessEntity: Carriers | undefined;
}): Temporal.ZonedDateTime {
  let uncheckedDate = date;
  while (!isValidBusinessDay({ date: uncheckedDate, businessEntity })) {
    uncheckedDate = uncheckedDate.add({ days: 1 });
  }
  return uncheckedDate;
}

/**
 * Returns the instant `businessDays` business days after `date`. Weekends and
 * the selected holiday calendar are skipped. `businessEntity` selects which
 * holiday calendar to apply; `undefined` uses the general US-holiday calendar.
 */
export function addBusinessDays({
  date,
  businessDays,
  businessEntity,
}: {
  date: Temporal.ZonedDateTime;
  businessDays: number;
  businessEntity: Carriers | undefined;
}): Temporal.ZonedDateTime {
  let current = date;
  let added = 0;
  while (added < businessDays) {
    current = maybeIncrementToNextBusinessDay({
      date: current.add({ days: 1 }),
      businessEntity,
    });
    added++;
  }
  return current;
}

export function isValidBusinessDay({
  date,
  businessEntity,
}: {
  date: Temporal.ZonedDateTime;
  businessEntity: Carriers | undefined;
}): boolean {
  return (
    date.dayOfWeek !== TemporalDayOfWeek.SATURDAY &&
    date.dayOfWeek !== TemporalDayOfWeek.SUNDAY &&
    !isUnitedStatesHoliday({ date, businessEntity })
  );
}

export function isUnitedStatesHoliday({
  date,
  businessEntity,
}: {
  date: Temporal.ZonedDateTime;
  businessEntity: Carriers | undefined;
}): boolean {
  switch (businessEntity) {
    case Carriers.FEDEX:
    case Carriers.FEDEX_SMART_POST:
      return isUnitedStatesHolidayForFedex({ date });
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return isUnitedStatesHolidayForUps({ date });
    case Carriers.USPS:
    case Carriers.USPS_RETURNS:
    case Carriers.USPS_SHIP:
      return isUnitedStatesHolidayForUsps({ date });
    case Carriers.AMAZON:
      return isUnitedStatesHolidayForAmazon({ date });
    case Carriers.DHL_EXPRESS:
    case Carriers.DHL_ECS:
    case Carriers.VEHO:
    case Carriers.APC:
    case Carriers.FIRST_MILE:
    case Carriers.AUSTRALIA_POST:
    case Carriers.CANADA_POST:
    case Carriers.GLS:
    case Carriers.ASENDIA:
    case Carriers.ROYAL_MAIL_V3:
    case Carriers.SPEE_DEE:
    case Carriers.PASSPORT_GLOBAL:
    case Carriers.LANDMARK_GLOBAL:
    case Carriers.ETA_EXPRESS:
    case Carriers.EPOST_GLOBAL:
    case Carriers.ONTRAC:
    case Carriers.GLOBALE:
    case Carriers.UPS_MAIL_INNOVATIONS:
    case Carriers.PUROLATOR:
    case Carriers.UNIUNI:
    case Carriers.STALLION_EXPRESS:
    case Carriers.ECOM_EXPRESS:
    case Carriers.CHITCHATS:
    case Carriers.DHL_PAKET:
    case Carriers.DHL_PARCEL:
    case Carriers.DPD:
    case Carriers.DPD_NL:
    case Carriers.COLIS_PRIVE:
    case Carriers.BPOST:
    case Carriers.CORREOS:
    case Carriers.POSTE_ITALIANE:
    case Carriers.AUSTRIA_POST:
    case Carriers.EVRI:
    case Carriers.POST_NL:
    case Carriers.INTELCOM:
    case Carriers.FLEET_OPTICS:
    case Carriers.ICS_COURIER:
    case Carriers.WHIZ_DELIVERY:
    case Carriers.TIKTOK_SHOP:
    case Carriers.CANPAR_EXPRESS:
    case Carriers.GOFO:
    case Carriers.P2P_GLOBAL:
    case undefined:
      return isUnitedStatesHolidayForOtherCarrier({ date });
    default:
      assertNever(businessEntity);
  }
}

function getNthDayOfWeekInMonth(
  year: number,
  month: number,
  dayOfWeek: number,
  n: number,
): Temporal.PlainDate {
  // Start with the first day of the month
  let date = Temporal.PlainDate.from({ year, month, day: 1 });

  // Find the first occurrence of the target day of week
  while (date.dayOfWeek !== dayOfWeek) {
    date = date.add({ days: 1 });
  }

  // Add (n-1) weeks to get to the nth occurrence
  if (n > 1) {
    date = date.add({ weeks: n - 1 });
  }

  return date;
}

function getLastDayOfWeekInMonth(
  year: number,
  month: number,
  dayOfWeek: number,
): Temporal.PlainDate {
  // Start with the last day of the month
  let date = Temporal.PlainDate.from({ year, month, day: 1 })
    .add({ months: 1 })
    .subtract({ days: 1 });

  // Find the last occurrence of the target day of week
  while (date.dayOfWeek !== dayOfWeek) {
    date = date.subtract({ days: 1 });
  }

  return date;
}

function isVeteransDay(date: Temporal.ZonedDateTime): boolean {
  const plainDate = date.toPlainDate();
  return plainDate.month === 11 && plainDate.day === 11;
}

function isThanksgiving(date: Temporal.ZonedDateTime): boolean {
  const plainDate = date.toPlainDate();
  // Thanksgiving is the 4th Thursday (dayOfWeek = 4) of November
  const thanksgivingDate = getNthDayOfWeekInMonth(plainDate.year, 11, 4, 4);
  return plainDate.equals(thanksgivingDate);
}

function isChristmas(date: Temporal.ZonedDateTime): boolean {
  const plainDate = date.toPlainDate();
  return plainDate.month === 12 && plainDate.day === 25;
}

function isNewYearsDay(date: Temporal.ZonedDateTime): boolean {
  const plainDate = date.toPlainDate();
  return plainDate.month === 1 && plainDate.day === 1;
}

function isMLKJrDay(date: Temporal.ZonedDateTime): boolean {
  const plainDate = date.toPlainDate();
  // MLK Jr. Day is the 3rd Monday (dayOfWeek = 1) of January
  const mlkDayDate = getNthDayOfWeekInMonth(plainDate.year, 1, 1, 3);
  return plainDate.equals(mlkDayDate);
}

function isPresidentsDay(date: Temporal.ZonedDateTime): boolean {
  const plainDate = date.toPlainDate();
  // Presidents' Day is the 3rd Monday (dayOfWeek = 1) of February
  const presidentsDayDate = getNthDayOfWeekInMonth(plainDate.year, 2, 1, 3);
  return plainDate.equals(presidentsDayDate);
}

function isLaborDay(date: Temporal.ZonedDateTime): boolean {
  const plainDate = date.toPlainDate();
  // Labor Day is the 1st Monday (dayOfWeek = 1) of September
  const laborDayDate = getNthDayOfWeekInMonth(plainDate.year, 9, 1, 1);
  return plainDate.equals(laborDayDate);
}

function isColumbusDay(date: Temporal.ZonedDateTime): boolean {
  const plainDate = date.toPlainDate();
  // Columbus Day/Indigenous Peoples' Day is the 2nd Monday (dayOfWeek = 1) of October
  const columbusDayDate = getNthDayOfWeekInMonth(plainDate.year, 10, 1, 2);
  return plainDate.equals(columbusDayDate);
}

function isMemorialDay(date: Temporal.ZonedDateTime): boolean {
  const plainDate = date.toPlainDate();
  // Memorial Day is the last Monday (dayOfWeek = 1) of May
  const memorialDayDate = getLastDayOfWeekInMonth(plainDate.year, 5, 1);
  return plainDate.equals(memorialDayDate);
}

function isJuneteenth(date: Temporal.ZonedDateTime): boolean {
  const plainDate = date.toPlainDate();
  return plainDate.month === 6 && plainDate.day === 19;
}

function isIndependenceDay(date: Temporal.ZonedDateTime): boolean {
  const plainDate = date.toPlainDate();
  return plainDate.month === 7 && plainDate.day === 4;
}

function isMothersDay(date: Temporal.ZonedDateTime): boolean {
  const plainDate = date.toPlainDate();
  // Mother's Day is the 2nd Sunday (dayOfWeek = 7) of May
  const mothersDayDate = getNthDayOfWeekInMonth(plainDate.year, 5, 7, 2);
  return plainDate.equals(mothersDayDate);
}

/**
 * https://www.fedex.com/content/dam/fedex/us-united-states/services/2025_FedEx_Holiday_Service_Schedule.pdf
 */
function isUnitedStatesHolidayForFedex({
  date,
}: {
  date: Temporal.ZonedDateTime;
}): boolean {
  return (
    isNewYearsDay(date) ||
    isMemorialDay(date) ||
    isIndependenceDay(date) ||
    isLaborDay(date) ||
    isThanksgiving(date) ||
    isChristmas(date)
  );
}

/**
 * https://www.ups.com/us/en/support/shipping-support/shipping-services/holiday-shipping-schedule
 */
function isUnitedStatesHolidayForUps({
  date,
}: {
  date: Temporal.ZonedDateTime;
}): boolean {
  return (
    isNewYearsDay(date) ||
    isMLKJrDay(date) ||
    isMemorialDay(date) ||
    isIndependenceDay(date) ||
    isLaborDay(date) ||
    isThanksgiving(date) ||
    isChristmas(date) ||
    isMothersDay(date)
  );
}

function isUnitedStatesHolidayForUsps({
  date,
}: {
  date: Temporal.ZonedDateTime;
}): boolean {
  return (
    isNewYearsDay(date) ||
    isMLKJrDay(date) ||
    isPresidentsDay(date) ||
    isMemorialDay(date) ||
    isJuneteenth(date) ||
    isIndependenceDay(date) ||
    isLaborDay(date) ||
    isColumbusDay(date) ||
    isVeteransDay(date) ||
    isThanksgiving(date) ||
    isChristmas(date)
  );
}

function isUnitedStatesHolidayForAmazon({
  date,
}: {
  date: Temporal.ZonedDateTime;
}): boolean {
  return (
    isNewYearsDay(date) ||
    isMLKJrDay(date) ||
    isMemorialDay(date) ||
    isIndependenceDay(date) ||
    isLaborDay(date) ||
    isThanksgiving(date) ||
    isChristmas(date)
  );
}

function isUnitedStatesHolidayForOtherCarrier({
  date,
}: {
  date: Temporal.ZonedDateTime;
}): boolean {
  return (
    isNewYearsDay(date) ||
    isMLKJrDay(date) ||
    isMemorialDay(date) ||
    isIndependenceDay(date) ||
    isLaborDay(date) ||
    isThanksgiving(date) ||
    isChristmas(date)
  );
}
